import { useMemo, useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { loadPageTranslation, TranslationSection } from '../../locales';

// Cache des traductions chargées
const translationCache: Record<string, Record<string, any>> = {};

const loadTranslation = async (
  lang: string, 
  section: TranslationSection,
  page: string
): Promise<any> => {
  const cacheKey = `${lang}-${section}-${page}`;
  
  if (translationCache[cacheKey]) {
    return translationCache[cacheKey];
  }

  try {
    const translation = await loadPageTranslation(lang as 'fr' | 'en', section, page);
    translationCache[cacheKey] = translation;
    return translation;
  } catch (error) {
    console.warn(`Translation file not found: ${lang}/${section}/${page}.json`, error);
    return {};
  }
};

const getNestedValue = (obj: any, path: string): any => {
  const value = path.split('.').reduce((current, key) => current?.[key], obj);
  return value || path;
};

/**
 * Hook pour charger les traductions d'une page spécifique
 * @param section - Section de l'application ('public', 'game', 'panel')
 * @param page - Nom de la page/composant (ex: "Home", "Dashboard", "SupportModal")
 */
export const usePageTranslation = (section: TranslationSection, page: string) => {
  const { language } = useLanguage();
  const [translations, setTranslations] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const trans = await loadTranslation(language, section, page);
      setTranslations(trans);
      setLoading(false);
    };
    load();
  }, [language, section, page]);

  const t = useMemo(() => {
    return (key: string): any => {
      return getNestedValue(translations, key);
    };
  }, [translations]);

  return { t, loading, currentLang: language };
};

export default usePageTranslation;
